import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { applyArea } from './geosorting_geometry';
const BASE = apiUrl("/api/headoffice/geosorting");
const button = {
  padding: '8px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 7,
  background: '#fff',
  color: '#1e293b',
  fontWeight: 600,
  cursor: 'pointer'
};
const freeAreaNumber = areas => {
  const used = new Set(areas.map(a => a.name));
  let n = 1;
  while (used.has(`Area ${n}`)) n++;
  return n;
};
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
function nextColor(areas) {
  let n = areas.length;
  while (true) {
    const color = '#' + [0, 2, 4].map(i => {
      const h = n * 137.508 % 360 / 60;
      const k = (i + h) % 6;
      return Math.round(255 * (0.52 - 0.34 * Math.max(-1, Math.min(k, 4 - k, 1)))).toString(16).padStart(2, '0');
    }).join('');
    if (!areas.some(a => a.color === color)) return color;
    n++;
  }
}
export default function Geosorting({
  token,
  onDirtyChange
}) {
  const [pharmacies, setPharmacies] = useState([]),
    [pharmacyId, setPharmacyId] = useState('');
  const [areas, setAreas] = useState([]),
    [selected, setSelected] = useState(''),
    [mode, setMode] = useState('select');
  const [loaded, setLoaded] = useState(false);
  const [revision, setRevision] = useState(0),
    [dirty, setDirty] = useState(false),
    [busy, setBusy] = useState(true);
  const [updatingPatients, setUpdatingPatients] = useState(false);
  const [patientResult, setPatientResult] = useState(null);
  const [deletionResult, setDeletionResult] = useState(null);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [codes, setCodes] = useState({}),
    [postcodeStatus, setPostcodeStatus] = useState('');
  const [showDeliveries, setShowDeliveries] = useState(true),
    [deliveryData, setDeliveryData] = useState(null);
  const [deliveryError, setDeliveryError] = useState(''),
    [deliveryLoading, setDeliveryLoading] = useState(false),
    [deliveryRefresh, setDeliveryRefresh] = useState(0);
  const deliveryGroupRef = useRef(null);
  const [history, setHistory] = useState([]);
  const host = useRef(null),
    mapRef = useRef(null),
    groupRef = useRef(null),
    live = useRef({});
  const pharmacy = pharmacies.find(p => p.id === pharmacyId);
  const active = areas.find(a => a.id === selected);
  const hasCoords = pharmacy && pharmacy.lat !== null && pharmacy.lng !== null;
  async function api(path, body, signal) {
    const res = await fetch(BASE + path, {
      method: body === undefined ? 'GET' : 'POST',
      signal,
      headers: {
        Authorization: `Bearer ${token || localStorage.getItem('token') || ''}`,
        ...(body === undefined ? {} : {
          'Content-Type': 'application/json'
        })
      },
      ...(body === undefined ? {} : {
        body: JSON.stringify(body)
      })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Geosorting request failed.');
    return data;
  }
  function change(next) {
    setHistory(h => [...h.slice(-29), areas]);
    setAreas(next);
    setDirty(true);
    setNotice('');
    setPatientResult(null);
    setError('');
  }
  function commit(layer, id) {
    try {
      const old = areas.find(a => a.id === id);
      const name = `Area ${freeAreaNumber(areas)}`;
      const a = {
        ...(old || {
          id: uid(),
          name,
          color: nextColor(areas)
        }),
        geometry: layer.toGeoJSON().geometry
      };
      const next = applyArea(areas, a);
      change(next);
      setSelected(a.id);
      setMode('select');
    } catch (e) {
      setError(e.message);
      setMode('select');
    }
  }
  live.current = {
    commit,
    setSelected
  };
  useEffect(() => {
    const c = new AbortController();
    api('/pharmacies', undefined, c.signal).then(d => {
      setPharmacies(d.pharmacies);
      setPharmacyId(d.pharmacies[0]?.id || '');
      if (!d.pharmacies.length) setBusy(false);
    }).catch(e => {
      if (e.name !== 'AbortError') {
        setError(e.message);
        setBusy(false);
      }
    });
    return () => c.abort();
    // The token is supplied by Head Office.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => {
    if (!pharmacyId) return undefined;
    const c = new AbortController();
    setPatientResult(null);
    setBusy(true);
    setLoaded(false);
    setRevision(0);
    setError('');
    setAreas([]);
    setSelected('');
    setCodes({});
    setHistory([]);
    setMode('select');
    setNotice('');
    api('/' + pharmacyId, undefined, c.signal).then(d => {
      setAreas(d.areas);
      setRevision(d.revision);
      setDirty(Boolean(d.needsNameSave));
      if (d.needsNameSave) setNotice('Existing area names have been numbered. Save areas to apply them.');
      setLoaded(true);
      setBusy(false);
    }).catch(e => {
      if (e.name !== 'AbortError') {
        setError(e.message);
        setBusy(false);
      }
    });
    return () => c.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, token]);
  useEffect(() => {
    setDeliveryData(null);
    setDeliveryError('');
    if (!pharmacyId || !showDeliveries) {
      setDeliveryLoading(false);
      return undefined;
    }
    const c = new AbortController();
    setDeliveryLoading(true);
    api(`/${pharmacyId}/delivery-overlay`, undefined, c.signal).then(data => {
      if (!c.signal.aborted) {
        setDeliveryData({
          ...data,
          pharmacyId
        });
        setDeliveryLoading(false);
      }
    }).catch(e => {
      if (!c.signal.aborted) {
        setDeliveryError(e.message);
        setDeliveryLoading(false);
      }
    });
    return () => c.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, token, showDeliveries, deliveryRefresh]);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const guard = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);
  useEffect(() => {
    if (!hasCoords || busy) return undefined;
    const map = L.map(host.current).setView([pharmacy.lat, pharmacy.lng], 13);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);
    L.circleMarker([pharmacy.lat, pharmacy.lng], {
      radius: 10,
      color: '#fff',
      weight: 3,
      fillColor: '#111827',
      fillOpacity: 1,
      pmIgnore: true
    }).addTo(map).bindTooltip(() => {
      const span = document.createElement('span');
      span.textContent = pharmacy.name;
      return span;
    }, {
      permanent: true,
      direction: 'top'
    });
    groupRef.current = L.featureGroup().addTo(map);
    map.on('pm:create', e => {
      map.removeLayer(e.layer);
      live.current.commit(e.layer);
    });
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(host.current);
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      groupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, hasCoords, busy]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !showDeliveries || deliveryData?.pharmacyId !== pharmacyId) return undefined;
    const group = L.featureGroup().addTo(map);
    deliveryGroupRef.current = group;
    for (const p of deliveryData.points) {
      const size = p.patients > 1 ? 32 : 16;
      const icon = L.divIcon({
        className: 'geosorting-delivery-marker',
        html: `<span class="geosorting-delivery-pin">${p.patients > 1 ? p.patients : ''}</span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
      const popup = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = p.postcode;
      popup.append(title, document.createElement('br'), document.createTextNode(`${p.patients} unique patient${p.patients === 1 ? '' : 's'} with a delivery log updated in the last three months`));
      L.marker([p.lat, p.lng], {
        icon,
        pmIgnore: true,
        title: `${p.postcode}: ${p.patients} unique patients`,
        zIndexOffset: 500
      }).bindPopup(popup).addTo(group);
    }
    return () => {
      group.remove();
      if (deliveryGroupRef.current === group) deliveryGroupRef.current = null;
    };
  }, [deliveryData, showDeliveries, pharmacyId, busy, hasCoords]);
  useEffect(() => {
    const group = groupRef.current,
      map = mapRef.current;
    if (!group || !map) return;
    group.clearLayers();
    areas.forEach(a => {
      const layer = L.geoJSON(a.geometry, {
        style: {
          color: a.color,
          weight: a.id === selected ? 4 : 2,
          fillOpacity: a.id === selected ? 0.36 : 0.2
        }
      });
      layer.eachLayer(part => {
        part.addTo(group);
        const label = document.createElement('span');
        label.textContent = a.name;
        part.bindTooltip(label);
        part.on('click', () => {
          if (mode === 'select') setSelected(a.id);
        });
        if (a.id === selected && mode === 'edit') part.pm.enable({
          allowSelfIntersection: false,
          snappable: true
        });
        if (a.id === selected && mode === 'move') part.pm.enableLayerDrag();
        let pending;
        const finish = () => {
          clearTimeout(pending);
          pending = setTimeout(() => live.current.commit(part, a.id), 0);
        };
        part.on('pm:markerdragend pm:dragend pm:vertexadded pm:vertexremoved', finish);
        part.on('remove', () => clearTimeout(pending));
      });
    });
    map.pm.disableDraw();
    if (mode === 'draw') map.pm.enableDraw('Polygon', {
      allowSelfIntersection: false,
      snappable: true,
      finishOn: 'dblclick',
      pathOptions: {
        color: nextColor(areas)
      }
    });
  }, [areas, selected, mode, busy]);
  useEffect(() => {
    if (!pharmacyId || busy || !areas.length) {
      setCodes({});
      setPostcodeStatus('');
      return undefined;
    }
    const c = new AbortController();
    setCodes({});
    setPostcodeStatus('Loading postcodes…');
    const timer = setTimeout(() => api(`/${pharmacyId}/postcodes`, {
      areas
    }, c.signal).then(d => {
      setCodes(d.byArea);
      setPostcodeStatus(d.message || 'Postcodes are assigned by their directory centre point.');
    }).catch(e => {
      if (e.name !== 'AbortError') setPostcodeStatus('Postcode lookup failed: ' + e.message);
    }), 300);
    return () => {
      clearTimeout(timer);
      c.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, pharmacyId, busy]);
  async function save(next = areas, deletedName = null) {
    setBusy(true);
    setError('');
    try {
      const d = await api(`/${pharmacyId}/save`, {
        areas: next,
        revision,
        nextAreaNumber: freeAreaNumber(next)
      });
      setAreas(d.areas);
      setRevision(d.revision);
      setDirty(false);
      setHistory([]);
      setPatientResult(null);
      setNotice('Areas saved.');
      const names = (d.deletedAreas || []).map(a => a.name);
      if (deletedName && !names.includes(deletedName)) names.push(deletedName);
      if (names.length) {
        setSelected('');
        setDeletionResult({
          names,
          count: d.removedFromRecords || 0
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setMode('select');
    }
  }
  async function updatePatientPopups() {
    setBusy(true);
    setUpdatingPatients(true);
    setError('');
    setPatientResult(null);
    setNotice('');
    try {
      const result = await api(`/${pharmacyId}/update-patient-scan`, {
        revision
      });
      setPatientResult(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setUpdatingPatients(false);
      setBusy(false);
      setMode('select');
    }
  }
  return <section className="geosorting-editor" style={{
    color: '#1e293b'
  }}>
    <style>{`
      .geosorting-delivery-marker { background: transparent; border: 0; }
      .geosorting-delivery-pin { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; border-radius: 50%; background: #b45309; color: white; -webkit-text-fill-color: white; border: 2px solid white; box-shadow: 0 1px 5px #0006; font: bold 12px Arial, sans-serif; }

      .geosorting-editor .geosorting-button {
        color: #1e293b !important;
        -webkit-text-fill-color: #1e293b !important;
        background: #ffffff !important;
        opacity: 1 !important;
        font-weight: 600;
        text-shadow: none !important;
      }
      .geosorting-editor .geosorting-button:not(:disabled):hover {
        background: #e2e8f0 !important;
      }
      .geosorting-editor .geosorting-button[aria-pressed="true"] {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        background: #1e40af !important;
        border-color: #1e40af !important;
      }
      .geosorting-editor .geosorting-button[aria-pressed="true"]:not(:disabled):hover {
        background: #1e3a8a !important;
      }
      .geosorting-editor .geosorting-button:disabled {
        color: #475569 !important;
        -webkit-text-fill-color: #475569 !important;
        background: #f1f5f9 !important;
        border-color: #cbd5e1 !important;
        cursor: not-allowed !important;
        opacity: 1 !important;
        box-shadow: none !important;
      }
      .geosorting-editor .geosorting-button:focus-visible {
        outline: 3px solid #2563eb !important;
        outline-offset: 3px;
      }
    `}</style>
    <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      alignItems: 'center',
      marginBottom: 12
    }}>
      <label>Pharmacy <select aria-label="Pharmacy" disabled={busy} value={pharmacyId} onChange={async e => {
          if (!dirty || (await appConfirm('Discard unsaved area changes and switch pharmacy?'))) {
            setDirty(false);
            setPharmacyId(e.target.value);
          }
        }} style={button}>{pharmacies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
      <button className="geosorting-button" style={button} disabled={!dirty || busy || !loaded} onClick={() => save()}>Save areas</button>
      <button className="geosorting-button" style={button} disabled={!history.length || busy} onClick={() => {
        setAreas(history[history.length - 1]);
        setHistory(h => h.slice(0, -1));
        setDirty(true);
        setMode('select');
      }}>Undo</button>
      <button className="geosorting-button" style={button} disabled={busy || !loaded || dirty || !areas.length || !revision} title={dirty ? 'Save your area changes first.' : 'Set delivery patients’ scan messages to their saved postcode area name.'} onClick={updatePatientPopups}>Update patient scan popup</button>
      <span role="status">{updatingPatients ? 'Updating patient scan popups…' : busy ? 'Loading…' : dirty ? 'Unsaved changes' : notice}</span>
    </div>
    {deletionResult && <div role="dialog" aria-modal="true" aria-label="Area deleted" style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15,23,42,.45)',
      zIndex: 10050,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff',
        padding: 24,
        borderRadius: 10,
        maxWidth: 480,
        margin: 16
      }}>
        <h3>Area deleted</h3>
        <p>{deletionResult.names.join(', ')} {deletionResult.names.length === 1 ? 'has' : 'have'} been deleted, and the area {deletionResult.names.length === 1 ? 'name removed' : 'names removed'} from {deletionResult.count} patient record{deletionResult.count === 1 ? '' : 's'}.</p>
        <button className="geosorting-button" style={button} autoFocus onClick={() => setDeletionResult(null)}>OK</button>
      </div>
    </div>}
    {patientResult && <div role="status" style={{
      margin: '10px 0',
      padding: 12,
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      borderRadius: 7
    }}>
      Updated {patientResult.updated} patient scan popups; {patientResult.alreadyCorrect} already correct.
      {' '}{patientResult.examined} eligible patients checked.
      {' '}No current area assigned: {patientResult.noPostcode} without a postcode at the end of the address,
      {' '}{patientResult.postcodeNotFound} absent from the postcode directory,
      {' '}{patientResult.outsideAreas} outside the saved areas,
      {' '}{patientResult.changedDuringUpdate} changed during the update.
    </div>}
    <p style={{
      fontSize: 13
    }}>Save your areas first, then use “Update patient scan popup” to remove all old Area labels and rebuild them from the current areas. Other scan-message text is preserved. Patients without a matching area have their old Area labels removed.</p>
    {error && <p role="alert" style={{
      color: '#b91c1c'
    }}>{error}</p>}
    {!busy && !pharmacies.length && <p>No pharmacies are available in your organisation.</p>}
    {pharmacy && !hasCoords && <p role="alert">This pharmacy needs valid lat/long coordinates in its pharmacy record before you can draw areas.</p>}
    <div style={{
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      marginBottom: 10
    }}>
      {[['draw', 'Draw area'], ['select', 'Select'], ['edit', 'Adjust vertices'], ['move', 'Move area']].map(([id, label]) => <button className="geosorting-button" aria-pressed={mode === id} key={id} style={{
        ...button,
        background: mode === id ? '#dbeafe' : '#fff'
      }} disabled={busy || !loaded || !hasCoords || ['edit', 'move'].includes(id) && !active} onClick={() => setMode(id)}>{label}</button>)}
      <button className="geosorting-button" style={button} disabled={!mapRef.current} onClick={() => mapRef.current?.setView([pharmacy.lat, pharmacy.lng], 13)}>Centre on pharmacy</button>
    </div>
    <p style={{
      fontSize: 13
    }}>Draw: click each corner, then click the first point to finish. Select an area to move it or adjust its corners. Area names are fixed; deleted numbers can be reused. Each completed adjustment trims neighbouring areas to meet its edge. A fully covered area is removed; Undo restores it.</p>
    <div style={{
      margin: '12px 0',
      padding: 12,
      background: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: 7
    }}>
      <label><input type="checkbox" checked={showDeliveries} onChange={e => setShowDeliveries(e.target.checked)} /> Show deliveries in the last three months</label>
      {' '}<button className="geosorting-button" style={button} disabled={!pharmacyId || !showDeliveries || deliveryLoading} onClick={() => setDeliveryRefresh(n => n + 1)}>Refresh deliveries</button>
      {' '}<button className="geosorting-button" style={button} disabled={busy || !showDeliveries || deliveryData?.pharmacyId !== pharmacyId || !deliveryData?.points.length || !hasCoords} onClick={() => {
        const bounds = deliveryGroupRef.current?.getBounds();
        if (bounds?.isValid()) mapRef.current?.fitBounds(bounds, {
          padding: [35, 35],
          maxZoom: 15
        });
      }}>Show all delivery postcodes</button>
      <p style={{
        fontSize: 13,
        marginBottom: 0
      }}>Orange markers show delivery patient postcodes. Numbers count unique patients with at least one delivery log updated within the last three calendar months, including unsuccessful deliveries. Each patient is counted once at their current postcode, regardless of how many delivery logs they have. Locations use the postcode at the end of the patient's current address, not their stored lat/long.</p>
      {showDeliveries && deliveryLoading && <p role="status">Loading recent delivery postcodes…</p>}
      {showDeliveries && deliveryError && <p role="alert" style={{
        color: '#b91c1c'
      }}>Delivery overlay could not load: {deliveryError}</p>}
      {showDeliveries && deliveryData?.pharmacyId === pharmacyId && <p role="status">
        {deliveryData.plottedPatients} unique patients · {deliveryData.points.length} postcodes plotted.
        {' '}Period: {new Date(deliveryData.since).toLocaleDateString('en-GB')} to {new Date(deliveryData.until).toLocaleDateString('en-GB')}.
        {' '}{deliveryData.missingPostcodePatients} patients without a postcode; {deliveryData.unlocatedPatients} without a usable postcode directory location.
        {!deliveryData.patients && ' No matching delivery patients have delivery logs in this period.'}
      </p>}
    </div>
    <div ref={host} aria-label="Pharmacy delivery area map" style={{
      height: '65vh',
      minHeight: 480,
      borderRadius: 10,
      border: '1px solid #cbd5e1',
      position: 'relative',
      zIndex: 0,
      background: '#f1f5f9',
      display: hasCoords && !busy ? 'block' : 'none'
    }} />
    <div style={{
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      marginTop: 12
    }}>
      {areas.map(a => <button className="geosorting-button" key={a.id} onClick={() => {
        setSelected(a.id);
        setMode('select');
      }} style={{
        ...button,
        border: `${selected === a.id ? 3 : 1}px solid ${a.color}`
      }}><span style={{
          color: a.color
        }}>●</span> {a.name}</button>)}
    </div>
    {!areas.length && !busy && hasCoords && <p>No areas yet. Choose “Draw area” to create your first one.</p>}
    {active && <div style={{
      marginTop: 18,
      padding: 16,
      border: `1px solid ${active.color}`,
      borderRadius: 10
    }}>
      <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div>Area name: <strong>{active.name}</strong> <span style={{
            color: '#475569',
            fontSize: 12
          }}>(fixed)</span></div>
        <label>Colour <input type="color" aria-label="Area colour" value={active.color} disabled={busy} onChange={e => {
            if (areas.some(a => a.id !== active.id && a.color === e.target.value)) {
              setError('Choose a different colour for each area.');
              return;
            }
            change(areas.map(a => a.id === active.id ? {
              ...a,
              color: e.target.value
            } : a));
          }} /></label>
        <button className="geosorting-button" style={button} disabled={busy} onClick={async () => {
          if ((await appConfirm(`Delete ${active.name}? This saves the current map and removes this area name from delivery patients’ scan messages at this pharmacy.`))) save(areas.filter(a => a.id !== selected), active.name);
        }}>Delete area</button>
      </div>
      <h3>Postcodes in {active.name} ({(codes[active.id] || []).length})</h3>
      <p role="status">{postcodeStatus}</p>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        maxHeight: 300,
        overflow: 'auto'
      }}>{(codes[active.id] || []).map(p => <span key={p} style={{
          padding: '6px 10px',
          background: '#f1f5f9',
          borderRadius: 5
        }}>{p}</span>)}</div>
      {!codes[active.id]?.length && postcodeStatus.startsWith('Postcodes are') && <p>No postcode centres fall within this area.</p>}
    </div>}
  </section>;
}
